import { useEffect, useRef, useState } from 'react';

/**
 * Advanced viewport collision & overlap protection hook.
 * Monitors container heights and viewport dimensions to prevent layout clipping.
 * Returns dynamic spacing/margin adjustments and container constraints.
 */
export function useResponsiveLayout() {
  const containerRef = useRef(null);
  const [constraints, setConstraints] = useState({
    maxHeight: '90vh',
    containerPadding: 'p-4',
    marginY: 'my-auto',
    flexWrap: 'flex-wrap',
    isCompressed: false,
    viewportHeight: 0,
    containerHeight: 0,
    safeZonePercentage: 0,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateLayout();
    });

    // Also listen to window resize for viewport changes
    const handleWindowResize = () => {
      calculateLayout();
    };

    const calculateLayout = () => {
      if (!containerRef.current) return;

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;
      const containerWidth = containerRect.width;

      // Calculate safe zone percentage
      const safeZonePercentage = (containerHeight / viewportHeight) * 100;

      // Determine if layout is compressed
      const isCompressed = viewportHeight < 600 || viewportWidth < 480;

      // Dynamic padding based on viewport
      let containerPadding = 'p-4';
      if (viewportHeight < 500) {
        containerPadding = 'p-2';
      } else if (viewportHeight < 700) {
        containerPadding = 'p-3';
      }

      // Dynamic max-height to ensure no clipping
      let maxHeight;
      if (viewportHeight < 500) {
        maxHeight = 'max-h-96'; // Reduced height for very small screens
      } else if (viewportHeight < 700) {
        maxHeight = 'max-h-[70vh]';
      } else {
        maxHeight = 'max-h-[85vh]';
      }

      // Dynamic margin to center and account for safe zones
      let marginY = 'my-auto';
      if (isCompressed) {
        marginY = 'my-2';
      }

      // Flex wrap behavior
      let flexWrap = 'flex-wrap';
      if (containerWidth > 640) {
        flexWrap = 'flex-nowrap';
      }

      setConstraints({
        maxHeight,
        containerPadding,
        marginY,
        flexWrap,
        isCompressed,
        viewportHeight,
        containerHeight,
        safeZonePercentage,
      });
    };

    // Initial calculation
    calculateLayout();

    // Observe container changes
    resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  return { containerRef, constraints };
}


